import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Globe } from 'lucide-react';
import { useLangLink, useSwitchLang, getLangFromPath } from '@/lib/langRouter';

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const l = useLangLink();
  const switchLang = useSwitchLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const nav = [
    { href: `${l('/')}#about`, label: t('nav.about') },
    { href: `${l('/')}#how`, label: t('nav.how') },
    { href: `${l('/')}#projects`, label: t('nav.projects') },
    { href: `${l('/')}#contact`, label: t('nav.contact') },
  ];

  return (
    <header className={`zx-header fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? 'bg-[#F6F3EE]/95 backdrop-blur-md shadow-[0_1px_0_rgba(11,30,63,0.08)]' : 'bg-transparent'}`}>
      <div className="max-w-[1280px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <Link to={l('/')} className="flex items-center gap-2.5 group" data-testid="header-logo">
          <svg width="36" height="36" viewBox="0 0 64 64" className="transition-transform group-hover:rotate-3">
            <defs>
              <linearGradient id="hlg" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#0B1E3F" />
                <stop offset="100%" stopColor="#1E3A8A" />
              </linearGradient>
            </defs>
            <rect width="64" height="64" rx="14" fill="url(#hlg)" />
            <rect x="14" y="36" width="8" height="18" fill="#E9B949" />
            <rect x="26" y="24" width="8" height="30" fill="#E9B949" />
            <rect x="38" y="14" width="8" height="40" fill="#E9B949" />
            <rect x="12" y="52" width="40" height="3" fill="#E9B949" />
          </svg>
          <div className="leading-tight">
            <div className="font-serif text-[20px] font-semibold text-[#0B1E3F]">Zyvanelix</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#0B1E3F]/60">Real Estate · HR</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((n) => (
            <a key={n.label} href={n.href} className="text-[14px] font-medium text-[#0B1E3F]/80 hover:text-[#0B1E3F] transition-colors" data-testid={`nav-${n.label}`}>
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center bg-white/60 border border-[#0B1E3F]/10 rounded-full p-1" role="group" aria-label={t('nav.language')}>
            <Globe size={14} className="ml-2 text-[#0B1E3F]/60" />
            {['hr', 'en'].map((code) => (
              <button
                key={code}
                onClick={() => switchLang(code)}
                className={`ml-1 px-2.5 py-1 text-[12px] font-semibold rounded-full transition-all ${lang === code ? 'bg-[#0B1E3F] text-white' : 'text-[#0B1E3F]/70 hover:text-[#0B1E3F]'}`}
                data-testid={`lang-switch-${code}`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>

          <a
            href={`${l('/')}#contact`}
            className="hidden lg:inline-flex items-center gap-1.5 bg-[#0B1E3F] hover:bg-[#12295a] text-white text-[13px] font-semibold px-5 py-2.5 rounded-full transition-colors"
            data-testid="header-cta"
          >
            {t('hero.ctaPrimary')}
          </a>

          <button
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/60 border border-[#0B1E3F]/10"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="header-hamburger"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F6F3EE] border-t border-[#0B1E3F]/10">
          <div className="px-5 py-6 flex flex-col gap-4">
            {nav.map((n) => (
              <a key={n.label} href={n.href} className="text-[15px] font-medium text-[#0B1E3F] py-1.5" data-testid={`mobile-nav-${n.label}`}>
                {n.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-4 border-t border-[#0B1E3F]/10">
              <span className="text-[12px] text-[#0B1E3F]/60 uppercase tracking-wider mr-2">{t('nav.language')}</span>
              {['hr', 'en'].map((code) => (
                <button
                  key={code}
                  onClick={() => switchLang(code)}
                  className={`px-3 py-1.5 text-[12px] font-semibold rounded-full transition ${lang === code ? 'bg-[#0B1E3F] text-white' : 'bg-white text-[#0B1E3F]/70'}`}
                  data-testid={`mobile-lang-${code}`}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
            <a
              href={`${l('/')}#contact`}
              className="mt-2 inline-flex justify-center bg-[#0B1E3F] text-white text-[14px] font-semibold px-5 py-3 rounded-full"
              data-testid="mobile-cta"
            >
              {t('hero.ctaPrimary')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
